// Expense statement (Round 8): the system supplies the COMPLETE inventory
// of verified cost amounts (deterministic — nothing forgotten); the USER
// curates which rows belong; the export carries the proof. Machine
// completeness, human judgment — same division of labor as the
// commitments table.
//
// Row source: verified amount facts only. Cost-flagged facts seed the
// statement (included by default); the remaining verified amounts are
// offered under "Add other amounts" (excluded by default) so a
// misclassified cost — the Round-4 rate-guardrail lesson — can be pulled
// in manually. Flow is a label elsewhere; HERE it picks defaults only,
// never hides a row from the pool.
//
// All arithmetic in integer cents via the shared utilities. No float
// sums, no AI-claimed totals, and component+total conflicts are PROMPTED,
// never silently summed.

import type { CaseRecord, RecordFact } from "../types";
import { displayFactTitle, formatFactDate, parseAmount } from "./factMeta";
import { dedupeFacts, normalizeForDedup, normalizeQuote } from "./dedupeFacts";
import {
  collapseSameRecordDuplicates,
  findExactFold,
  flowOf,
  type FinancialLine,
} from "./financialFlow";
import { formatCents, toCents } from "./costsMath";
import { PROVENANCE_LEGEND } from "./provenance";

export interface ExpenseRow {
  fact: RecordFact;
  // Other records carrying the same deduplicated fact.
  alsoIn: string[];
  // Same-record duplicates collapsed into this row (Round 5 rule); their
  // quotes stay accessible.
  duplicates: RecordFact[];
  // Stable content key — survives re-extraction id churn.
  key: string;
  cents: number;
  // Whether the row seeds the statement (cost flow) or lives under
  // "Add other amounts".
  seeded: boolean;
}

// Content key: record + canonical cents + normalized quote head. Fact ids
// regenerate on re-extraction; the verified quote and amount are anchored
// to the source document, so the key survives. A reworded quote loses the
// override — the row returns to its default, the safe failure direction.
export function expenseFactKey(f: RecordFact): string {
  return [
    f.record_id,
    toCents(parseAmount(f.amount)),
    normalizeQuote(f.verbatim_quote).slice(0, 80),
  ].join("|");
}

export interface ExpenseRowPool {
  seeded: ExpenseRow[];
  other: ExpenseRow[];
}

// Deterministic row pool. Verified, non-zero amounts only; quote-deduped
// across records (forwarded copies collapse); same-record duplicate
// amounts with near-identical titles collapse (Round 5 rule) BEFORE the
// duplicate prompt ever sees them. Chronological order.
export function buildExpenseRows(
  facts: RecordFact[],
  recordById: Map<string, CaseRecord>,
): ExpenseRowPool {
  const amounts = facts.filter(
    (f) =>
      f.fact_type === "amount" &&
      f.verified === 1 &&
      !!f.amount?.trim() &&
      parseAmount(f.amount) !== 0,
  );
  const deduped = dedupeFacts(amounts, recordById);

  // Same-record collapse via the shared financial-summary rule.
  const byRecord = new Map<string, FinancialLine[]>();
  for (const d of deduped) {
    const g = byRecord.get(d.fact.record_id) ?? [];
    g.push({ fact: d.fact, alsoIn: d.alsoIn, components: [], duplicates: [] });
    byRecord.set(d.fact.record_id, g);
  }
  const lines: FinancialLine[] = [];
  for (const g of byRecord.values()) lines.push(...collapseSameRecordDuplicates(g));

  const rows: ExpenseRow[] = lines.map((l) => ({
    fact: l.fact,
    alsoIn: l.alsoIn,
    duplicates: l.duplicates,
    key: expenseFactKey(l.fact),
    cents: toCents(parseAmount(l.fact.amount)),
    seeded: flowOf(l.fact) === "cost",
  }));
  rows.sort((a, b) => {
    const da = a.fact.date ?? "9999";
    const db = b.fact.date ?? "9999";
    return da < db ? -1 : da > db ? 1 : a.fact.summary.localeCompare(b.fact.summary);
  });

  return {
    seeded: rows.filter((r) => r.seeded),
    other: rows.filter((r) => !r.seeded),
  };
}

// Per-row user state (Round 9): the include/exclude override plus
// user-entered annotations — a free-text note and an "estimated" flag.
// Content-keyed like the include override; never touches the fact.
export interface RowOverride {
  included?: boolean;
  note?: string;
  estimated?: boolean;
}
export type OverrideMap = ReadonlyMap<string, RowOverride>;

// Effective inclusion: overrides win; otherwise seeded rows are in, others
// are out.
export function isIncluded(row: ExpenseRow, overrides: OverrideMap): boolean {
  return overrides.get(row.key)?.included ?? row.seeded;
}

export function rowNote(row: ExpenseRow, overrides: OverrideMap): string {
  return overrides.get(row.key)?.note ?? "";
}

export function isEstimated(row: ExpenseRow, overrides: OverrideMap): boolean {
  return overrides.get(row.key)?.estimated ?? false;
}

export interface DuplicateFlag {
  a: ExpenseRow;
  b: ExpenseRow;
  amountLabel: string;
}

export interface FoldConflict {
  total: ExpenseRow;
  components: ExpenseRow[];
}

export interface StatementComputation {
  included: ExpenseRow[];
  // Integer cents; null while a fold conflict is unresolved — never sum
  // a stated total and its components silently.
  totalCents: number | null;
  duplicateFlags: DuplicateFlag[];
  foldConflicts: FoldConflict[];
}

export function computeStatement(
  pool: ExpenseRowPool,
  overrides: OverrideMap,
): StatementComputation {
  const all = [...pool.seeded, ...pool.other];
  const included = all.filter((r) => isIncluded(r, overrides));

  // Fold conflicts: within one record, an included row whose cents equal
  // the exact sum of >= 2 other included rows.
  const foldConflicts: FoldConflict[] = [];
  const byRecord = new Map<string, ExpenseRow[]>();
  for (const r of included) {
    const g = byRecord.get(r.fact.record_id) ?? [];
    g.push(r);
    byRecord.set(r.fact.record_id, g);
  }
  for (const g of byRecord.values()) {
    const fold = findExactFold(g.map((r) => r.cents));
    if (fold) {
      foldConflicts.push({
        total: g[fold.totalIndex]!,
        components: fold.componentIndices.map((i) => g[i]!),
      });
    }
  }

  // Duplicate prompt: any two INCLUDED rows with identical cents (the
  // same-record near-identical pairs were already collapsed). Flag only —
  // never auto-exclude. Pairs inside a detected fold relationship are the
  // fold prompt's business, not a duplicate flag.
  const inFold = new Set<string>();
  for (const fc of foldConflicts) {
    inFold.add(fc.total.key);
    for (const c of fc.components) inFold.add(c.key);
  }
  const duplicateFlags: DuplicateFlag[] = [];
  for (let i = 0; i < included.length; i++) {
    for (let j = i + 1; j < included.length; j++) {
      const a = included[i]!;
      const b = included[j]!;
      if (a.cents !== b.cents) continue;
      if (inFold.has(a.key) && inFold.has(b.key)) continue;
      duplicateFlags.push({ a, b, amountLabel: formatCents(a.cents) });
    }
  }

  const totalCents =
    foldConflicts.length > 0
      ? null
      : included.reduce((s, r) => s + r.cents, 0);

  return { included, totalCents, duplicateFlags, foldConflicts };
}

// ---------------------------------------------------------------------------
// Same-vendor overlap prompt (Round 9, 4C). Real-world costs evolve: an
// original agreement, an amended agreement with new charges, an estimate
// through a future date. When included rows come from records sharing one
// vendor/agreement identity (same normalized record-title root), they may
// describe the SAME expense at different times. Flag only — never
// auto-exclude. Equal-amount pairs are the duplicate prompt's business, so
// a group whose rows are all the same cents is skipped here.
// ---------------------------------------------------------------------------

export interface VendorFlag {
  // The shared normalized title root, for display.
  vendorLabel: string;
  rows: ExpenseRow[];
  // Rows from the most recent source record — "keep the most recent" keeps
  // exactly these.
  latest: ExpenseRow[];
  // Stable key for a "keep all" dismissal.
  key: string;
}

// Normalized record-title root: forwarding/reply/system prefixes stripped,
// whitespace collapsed, lowercased. The two "Fw: Enterprise Rental
// Agreement 3M28CS" records (original + amended) share one root.
export function recordTitleRoot(subject: string | null | undefined): string {
  const stripped = (subject ?? "").replace(
    /^(?:\s*(?:\{external\}|fwd?\s*:|re\s*:))+\s*/i,
    "",
  );
  return normalizeForDedup(stripped);
}

export function computeVendorFlags(
  included: ExpenseRow[],
  recordById: Map<string, CaseRecord>,
  dismissed?: ReadonlySet<string>,
): VendorFlag[] {
  const byRoot = new Map<string, ExpenseRow[]>();
  for (const r of included) {
    const root = recordTitleRoot(recordById.get(r.fact.record_id)?.subject);
    if (!root) continue;
    const g = byRoot.get(root) ?? [];
    g.push(r);
    byRoot.set(root, g);
  }
  const flags: VendorFlag[] = [];
  for (const [root, rows] of byRoot) {
    const recordIds = new Set(rows.map((r) => r.fact.record_id));
    if (recordIds.size < 2) continue;
    // All-equal cents → already covered by the duplicate prompt.
    if (rows.every((r) => r.cents === rows[0]!.cents)) continue;
    const latestRecordId = [...recordIds].reduce((best, id) => {
      const db = recordById.get(best)?.record_date ?? "";
      const di = recordById.get(id)?.record_date ?? "";
      return di > db ? id : best;
    });
    const key = `${root}|${rows.map((r) => r.key).sort().join("|")}`;
    if (dismissed?.has(key)) continue;
    flags.push({
      vendorLabel: root,
      rows,
      latest: rows.filter((r) => r.fact.record_id === latestRecordId),
      key,
    });
  }
  return flags;
}

// ---------------------------------------------------------------------------
// Export document (Round 6 pattern: the exporters print these strings
// verbatim; a test pins the cents-exact figures).
// ---------------------------------------------------------------------------

export interface StatementDoc {
  title: string;
  dateLine: string;
  // 4 columns (Date, Description, Amount, Source); a 5th "Notes" column
  // appears when any included row carries a user note (Round 9, 4A).
  headers: string[];
  rows: string[][];
  // "Total: $X" — or "Total (includes estimated amounts): $X" when any
  // included row is flagged estimated, NEVER a bare total silently mixing
  // finals and estimates (Round 9, 4B).
  totalLine: string;
  footer: string;
}

export const STATEMENT_FOOTER =
  "Each amount above is supported by the cited source document and was mechanically verified against it. " +
  `Descriptions are AI-generated summaries. ${PROVENANCE_LEGEND}`;

export function buildStatementDoc(
  caseName: string,
  included: ExpenseRow[],
  totalCents: number,
  recordById: Map<string, CaseRecord>,
  statementDateIso: string,
  overrides: OverrideMap = new Map(),
): StatementDoc {
  const anyNote = included.some((r) => rowNote(r, overrides));
  const anyEstimated = included.some((r) => isEstimated(r, overrides));
  return {
    title: `Expense statement — ${caseName}`,
    dateLine: `Statement date: ${formatFactDate(statementDateIso.slice(0, 10))}`,
    headers: ["Date", "Description", "Amount", "Source", ...(anyNote ? ["Notes"] : [])],
    rows: included.map((r) => {
      const rec = recordById.get(r.fact.record_id);
      const recTitle = rec?.subject?.trim() || "Record";
      const recDate = rec?.record_date
        ? formatFactDate(rec.record_date.slice(0, 10))
        : "—";
      const estimated = isEstimated(r, overrides);
      return [
        formatFactDate(r.fact.date),
        displayFactTitle(r.fact),
        `${formatCents(r.cents)}${estimated ? " (estimated)" : ""}`,
        `${recTitle} (${recDate})`,
        ...(anyNote ? [rowNote(r, overrides)] : []),
      ];
    }),
    totalLine: anyEstimated
      ? `Total (includes estimated amounts): ${formatCents(totalCents)}`
      : `Total: ${formatCents(totalCents)}`,
    footer: STATEMENT_FOOTER,
  };
}
